import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { resolveImage } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const { content } = useContent();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const brand = content?.brand || {};

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created — welcome to TMF!");
      nav("/", { replace: true });
    } catch (er) { setErr(formatApiError(er.response?.data?.detail) || "Registration failed"); }
    finally { setBusy(false); }
  };

  const google = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect_uri = window.location.origin + "/auth/google";
    try {
      const { data } = await api.post("/auth/google/start", { redirect_uri });
      window.location.href = data.authorize_url;
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Unable to start Google sign-in");
    }
  };

  return (
    <div className="min-h-[70vh] grid lg:grid-cols-2">
      <div className="hidden lg:block bg-tmf-navy relative grid-bg">
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-white border-2 border-tmf-sky">
              {brand.logo_url ? (
                <img src={resolveImage(brand.logo_url)} alt="TMF" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-tmf-blue text-white font-display font-black text-xl">T</div>
              )}
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tight">{brand.name || "TMF"}</div>
              <div className="text-[10px] font-mono-tmf tracking-widest text-tmf-sky uppercase">{brand.tagline || "by Silver Sure"}</div>
            </div>
          </Link>
          <div>
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-sky">Trade Portal</div>
            <div className="mt-2 font-display font-black text-4xl uppercase tracking-tight leading-none">Save up to 40%<br />on hospital-grade furniture.</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-white border border-slate-200">
              {brand.logo_url ? (
                <img src={resolveImage(brand.logo_url)} alt="TMF" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-tmf-blue text-white font-display font-black text-lg">T</div>
              )}
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight text-tmf-navy">{brand.name || "TMF"}</div>
              <div className="text-[10px] font-mono-tmf tracking-widest text-slate-500 uppercase">{brand.tagline || "by Silver Sure"}</div>
            </div>
          </Link>

          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Register</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-8 text-tmf-navy">Create Account</h1>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Full Name</label>
              <Input data-testid="register-name" required value={form.name} onChange={setF("name")} className="rounded-none mt-1 border-slate-300 h-11" />
            </div>
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Email</label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={setF("email")} className="rounded-none mt-1 border-slate-300 h-11" />
            </div>
            <div>
              <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Password (min 6 chars)</label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={setF("password")} className="rounded-none mt-1 border-slate-300 h-11" />
            </div>
          </div>

          {err && <div data-testid="register-error" className="mt-4 text-sm text-tmf-orange border border-orange-300 bg-orange-50 px-3 py-2">{err}</div>}

          <Button data-testid="register-submit" type="submit" disabled={busy} className="rounded-none w-full mt-6 bg-tmf-blue hover:bg-tmf-navy h-12 uppercase tracking-widest text-sm font-bold">
            {busy ? "Creating account…" : "Sign Up"}
          </Button>

          <div className="relative my-6"><div className="border-t border-slate-200" /><span className="absolute inset-x-0 -top-2 text-center"><span className="bg-white px-3 text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">or</span></span></div>

          <button data-testid="google-register-btn" type="button" onClick={google} className="w-full border border-slate-300 hover:border-tmf-blue hover:bg-slate-50 py-3 flex items-center justify-center gap-3 text-sm font-medium">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.4 13.7 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.7-9.7 6.7-17.2z"/><path fill="#FBBC05" d="M10.4 28.4c-.5-1.5-.8-3.1-.8-4.7 0-1.6.3-3.2.8-4.7l-7.9-6.1C.9 16.1 0 20 0 24s.9 7.9 2.5 11.1l7.9-6.7z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.7l-7.3-5.7c-2 1.4-4.7 2.3-8.6 2.3-6.3 0-11.6-4.2-13.5-9.9L2.5 35.1C6.5 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>

          <p className="mt-8 text-sm text-slate-600 text-center">
            Already have an account? <Link to="/login" className="text-tmf-blue font-bold hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
